const RECHARGE_PRODUCT_FORM_SYNC_DEBUG = false;

function rechargeProductFormSyncDebugLog(context, message, payload) {
  if (!RECHARGE_PRODUCT_FORM_SYNC_DEBUG) return;

  const prefix = `[recharge-product-form-sync:${context}]`;

  if (typeof payload === 'undefined') {
    console.log(prefix, message);
    return;
  }

  console.log(prefix, message, payload);
}

/**
 * This script is used to enable accelerated checkout for Recharge products. It will
 * listen for changes in the custom selectors (payment options, delivery frequency, absorbency, bundle variant)
 * and sync the form state to the Recharge API such that the embedded Shopify form has the correct variant and bundle information.
 */
class RechargeProductFormSync extends HTMLElement {
  /**
   * Sets up bound handlers and caches used for sync throttling and bundle-id memoization. (only request bundle id once per bundle configuration)
   */
  constructor() {
    super();

    this.handleRelevantChange = this.handleRelevantChange.bind(this);
    this.handleMutation = this.handleMutation.bind(this);
    this.syncTimer = null;
    this.syncVersion = 0;
    this.boundElements = new WeakMap();
    this.activeMutationObserver = null;
    this.lastRenderedStateKey = null;
    this.lastBundleRequestKey = null;
    this.lastBundleState = null;
    this.pendingBundleRequests = new Map();
  }

  /**
   * Writes a debug log when the file-level debug flag is enabled.
   *
   * @param {string} message
   * @param {Object} [payload]
   */
  debug(message, payload) {
    rechargeProductFormSyncDebugLog(this.productId || this.sectionId || 'unknown', message, payload);
  }

  /**
   * Reads the product context from element attributes, caches the hidden form inputs,
   * wires listeners, and kicks off the initial form synchronization.
   */
  connectedCallback() {
    this.productId = this.getAttribute('product-id') || '';
    this.productFormId = this.getAttribute('product-form-id') || '';
    this.sectionId = this.getAttribute('section-id') || '';
    this.initialVariantId = this.getAttribute('initial-variant-id') || '';
    this.productType = this.getAttribute('product-type') || '';
    this.isAbsorbencyProduct = this.getAttribute('is-absorbency-product') === 'true';
    this.rechargeBundleCollectionId = this.getAttribute('recharge-bundle-collection-id') || '';
    this.rechargeBundleSubscriptionProductId =
      this.getAttribute('recharge-bundle-subscription-product-id') || '';
    this.oneTimePurchaseProductId = this.getAttribute('one-time-purchase-product-id') || '';
    this.oneTimePurchaseVariantId = this.getAttribute('one-time-purchase-variant-id') || '';
    this.hasDeliveryFrequencyProducts = this.getAttribute('has-delivery-frequency-products') === 'true';
    this.hasRegularRechargeSellingPlan =
      this.getAttribute('has-regular-recharge-selling-plan') === 'true';
    this.initialStandardSellingPlanId = this.getAttribute('initial-standard-selling-plan-id') || '';
    this.isStandardRechargeProduct =
      !this.hasDeliveryFrequencyProducts && this.hasRegularRechargeSellingPlan;

    this.form = this.querySelector('form');
    this.variantInput = this.form?.querySelector('input[name="id"]') || null;
    this.quantityInput = this.form?.querySelector('input[name="quantity"]') || null;
    this.sellingPlanInput = this.form?.querySelector('[name="selling_plan"]') || null;
    this.submitButton = this.form?.querySelector('button[name="add"]') || null;
    this.propertiesContainer = this.querySelector('[data-properties-container]') || null;

    this.syncRoot =
      document.getElementById(`shopify-section-${this.sectionId}`) ||
      this.closest('.shopify-section') ||
      this.closest('section') ||
      document;

    this.attachStaticListeners();
    this.attachDynamicListeners();
    this.observeForComponents();
    this.debug('Initialized sync component', {
      productId: this.productId,
      productFormId: this.productFormId,
      sectionId: this.sectionId,
      productType: this.productType,
      isAbsorbencyProduct: this.isAbsorbencyProduct,
      hasDeliveryFrequencyProducts: this.hasDeliveryFrequencyProducts,
      hasRegularRechargeSellingPlan: this.hasRegularRechargeSellingPlan,
      isStandardRechargeProduct: this.isStandardRechargeProduct,
      initialVariantId: this.initialVariantId,
      initialStandardSellingPlanId: this.initialStandardSellingPlanId,
      hasPaymentOptionsSelector: Boolean(this.getPaymentOptionsSelector()),
      hasDeliveryFrequencyDropdown: Boolean(this.getDeliveryFrequencyDropdown()),
      hasAbsorbencySelector: Boolean(this.getAbsorbencySelector()),
      hasBundleVariantSelector: Boolean(this.getBundleVariantSelector())
    });
    this.scheduleSync();
  }

  /**
   * Tears down timers and observers when the element is removed from the page.
   */
  disconnectedCallback() {
    clearTimeout(this.syncTimer);

    if (this.activeMutationObserver) {
      this.activeMutationObserver.disconnect();
      this.activeMutationObserver = null;
    }

    document.removeEventListener('variant:change', this.handleRelevantChange);
  }

  /**
   * Subscribes to document-level events and the visible quantity input that are expected
   * to exist when the component initializes.
   */
  attachStaticListeners() {
    document.addEventListener('variant:change', this.handleRelevantChange);

    const quantitySelector = this.getVisibleQuantityInput();
    if (quantitySelector) {
      quantitySelector.addEventListener('change', this.handleRelevantChange);
      quantitySelector.addEventListener('input', this.handleRelevantChange);
      this.boundElements.set(quantitySelector, true);
    }
  }

  /**
   * Watches the section for late-mounted custom selectors and variant switchers so the
   * sync layer can attach to them once they appear.
   */
  observeForComponents() {
    if (this.activeMutationObserver) {
      this.activeMutationObserver.disconnect();
    }

    this.activeMutationObserver = new MutationObserver(this.handleMutation);
    this.activeMutationObserver.observe(this.syncRoot === document ? document.body : this.syncRoot, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Re-scans for unbound controls after a DOM mutation and only schedules a sync if a
   * newly-added relevant control was found.
   */
  handleMutation() {
    const attachedNewListeners = this.attachDynamicListeners();
    if (attachedNewListeners) {
      this.debug('Detected newly-mounted relevant controls; scheduling sync');
      this.scheduleSync();
    }
  }

  /**
   * Attaches change listeners to the custom selectors and variant switchers used to build
   * the hidden Shopify form state.
   *
   * @returns {boolean} True when at least one new listener was attached.
   */
  attachDynamicListeners() {
    let attachedNewListeners = false;

    [
      this.getPaymentOptionsSelector(),
      this.getDeliveryFrequencyDropdown(),
      this.getAbsorbencySelector(),
      this.getBundleVariantSelector(),
      ...this.getVariantSwitchers()
    ]
      .filter(Boolean)
      .forEach((element) => {
        if (this.boundElements.has(element)) return;

        if (element.tagName === 'PAYMENT-OPTIONS-SELECTOR') {
          element.addEventListener('payment-options:change', this.handleRelevantChange);
        } else if (element.tagName === 'DELIVERY-FREQUENCY-DROPDOWN') {
          element.addEventListener('delivery-frequency:change', this.handleRelevantChange);
        } else if (element.tagName === 'ABSORBENCY-SELECTOR') {
          element.addEventListener('absorbency:change', this.handleRelevantChange);
        } else if (element.tagName === 'BUNDLE-VARIANT-SELECTOR') {
          element.addEventListener('bundle-variant:change', this.handleRelevantChange);
        } else {
          element.addEventListener('change', this.handleRelevantChange);
        }

        this.boundElements.set(element, true);
        attachedNewListeners = true;
        this.debug('Attached listener to control', {
          tagName: element.tagName,
          id: element.id || null
        });
      });

    const quantitySelector = this.getVisibleQuantityInput();
    if (quantitySelector && !this.boundElements.has(quantitySelector)) {
      quantitySelector.addEventListener('change', this.handleRelevantChange);
      quantitySelector.addEventListener('input', this.handleRelevantChange);
      this.boundElements.set(quantitySelector, true);
      attachedNewListeners = true;
      this.debug('Attached listener to quantity input', {
        id: quantitySelector.id || null
      });
    }

    return attachedNewListeners;
  }

  /**
   * Debounces all relevant UI changes through a single sync path.
   */
  handleRelevantChange(event) {
    this.debug('Relevant change detected', {
      eventType: event?.type || null,
      targetTagName: event?.target?.tagName || null,
      targetId: event?.target?.id || null
    });
    this.scheduleSync();
  }

  /**
   * Coalesces rapid UI updates into one sync pass so the hidden form is not recomputed
   * on every intermediate interaction.
   */
  scheduleSync() {
    clearTimeout(this.syncTimer);
    this.debug('Scheduling sync');
    this.syncTimer = setTimeout(() => {
      this.syncFormState();
    }, 50);
  }

  /**
   * Returns the custom payment options selector for this product, if present.
   *
   * @returns {HTMLElement|null}
   */
  getPaymentOptionsSelector() {
    return document.getElementById(`payment-options-selector-${this.productId}`);
  }

  /**
   * Returns the delivery frequency dropdown for this product, if present.
   *
   * @returns {HTMLElement|null}
   */
  getDeliveryFrequencyDropdown() {
    return document.getElementById(`delivery-frequency-dropdown-${this.productId}`);
  }

  /**
   * Returns the absorbency selector for this product, if present.
   *
   * @returns {HTMLElement|null}
   */
  getAbsorbencySelector() {
    return document.getElementById(`absorbency-selector-${this.productId}`);
  }

  /**
   * Returns the bundle variant selector for this product, if present.
   *
   * @returns {HTMLElement|null}
   */
  getBundleVariantSelector() {
    return document.getElementById(`bundle-variant-selector-${this.productId}`);
  }

  /**
   * Returns the visible quantity input that should stay in sync with the hidden form.
   *
   * @returns {HTMLInputElement|null}
   */
  getVisibleQuantityInput() {
    return document.getElementById(`Quantity-${this.productId}`);
  }

  /**
   * Finds any native theme variant switchers within the current section so the sync layer
   * can follow the active Shopify variant when the product is not bundle-driven.
   *
   * @returns {HTMLElement[]}
   */
  getVariantSwitchers() {
    if (!this.syncRoot || this.syncRoot === document) {
      return Array.from(document.querySelectorAll('variant-radios, variant-selects'));
    }

    return Array.from(this.syncRoot.querySelectorAll('variant-radios, variant-selects'));
  }

  /**
   * Normalizes the purchase type coming from the payment options selector.
   *
   * @returns {'subscription'|'one-time'}
   */
  getCurrentPaymentType() {
    return this.getPaymentOptionsSelector()?.value === 'subscription' ? 'subscription' : 'one-time';
  }

  /**
   * Reads the visible quantity input and falls back to the hidden form quantity if needed.
   *
   * @returns {number}
   */
  getVisibleQuantity() {
    const visibleQuantity = this.getVisibleQuantityInput();
    const quantity = parseInt(visibleQuantity?.value || this.quantityInput?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /**
   * Resolves the currently selected Shopify variant id by preferring active theme switchers,
   * then the existing theme form, then the initial server-rendered variant.
   *
   * @returns {string}
   */
  getBaseVariantId() {
    const variantSwitchers = this.getVariantSwitchers();
    const activeVariant = variantSwitchers.find((switcher) => switcher.currentVariant?.id)?.currentVariant;
    if (activeVariant?.id) {
      return String(activeVariant.id);
    }

    const existingThemeForm =
      this.sectionId && document.getElementById(`product-form-${this.sectionId}`);

    const existingThemeVariantId =
      existingThemeForm?.querySelector('input[name="id"]')?.value ||
      this.variantInput?.value ||
      this.initialVariantId;

    return String(existingThemeVariantId || '');
  }

  /**
   * Enables or disables the hidden add button that backs accelerated checkout availability.
   *
   * @param {boolean} disabled
   */
  setSubmitDisabled(disabled) {
    if (!this.submitButton) return;

    const wasDisabled =
      this.submitButton.hasAttribute('disabled') ||
      this.submitButton.getAttribute('aria-disabled') === 'true';

    if (disabled) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.setAttribute('aria-disabled', 'true');
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.setAttribute('aria-disabled', 'false');
    }

    if (wasDisabled !== disabled) {
      this.debug('Updated submit disabled state', { disabled });
    }
  }

  /**
   * Writes the selling plan into the hidden form and disables the input when no plan applies.
   *
   * @param {string|null} value
   */
  setSellingPlan(value) {
    if (!this.sellingPlanInput) return;

    const nextValue = value ? String(value) : '';
    const previousValue = this.sellingPlanInput.value;
    const wasDisabled = this.sellingPlanInput.disabled;

    if (
      this.sellingPlanInput.tagName === 'SELECT' &&
      nextValue &&
      !this.sellingPlanInput.querySelector(`option[value="${CSS.escape(nextValue)}"]`)
    ) {
      const option = document.createElement('option');
      option.value = nextValue;
      option.textContent = nextValue;
      this.sellingPlanInput.appendChild(option);
    }

    if (this.sellingPlanInput.tagName === 'SELECT') {
      this.sellingPlanInput.value = nextValue;
      this.sellingPlanInput.disabled = false;
    } else if (value) {
      this.sellingPlanInput.value = nextValue;
      this.sellingPlanInput.disabled = false;
    } else {
      this.sellingPlanInput.value = '';
      this.sellingPlanInput.disabled = true;
    }

    if (previousValue !== this.sellingPlanInput.value || wasDisabled !== this.sellingPlanInput.disabled) {
      this.debug('Updated selling plan input', {
        tagName: this.sellingPlanInput.tagName,
        previousValue,
        nextValue: this.sellingPlanInput.value,
        disabled: this.sellingPlanInput.disabled
      });
      this.sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Rebuilds the hidden line-item property inputs used by Recharge bundle checkout.
   *
   * @param {Record<string, string|number>} properties
   */
  setProperties(properties = {}) {
    if (!this.propertiesContainer) return;

    this.propertiesContainer.innerHTML = '';

    Object.entries(properties).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = String(value);
      this.propertiesContainer.appendChild(input);
    });

    this.debug('Updated line item properties', { properties });
  }

  /**
   * Returns a shallow clone of bundle state so cached or shared results are not mutated later.
   *
   * @param {Object} bundleState
   * @returns {{variantId: string, sellingPlanId: string|null, properties: Object, valid: boolean}}
   */
  cloneBundleState(bundleState = {}) {
    return {
      variantId: bundleState.variantId || '',
      sellingPlanId: bundleState.sellingPlanId || null,
      properties: { ...(bundleState.properties || {}) },
      valid: Boolean(bundleState.valid)
    };
  }

  /**
   * Serializes a state fragment into a stable string key for memoization and change detection.
   *
   * @param {Object} value
   * @returns {string}
   */
  buildStateKey(value) {
    return JSON.stringify(value || {});
  }

  /**
   * Computes the next hidden form state from the current custom selectors and updates the
   * Shopify product form only when the effective payload has changed.
   */
  async syncFormState() {
    const version = ++this.syncVersion;
    this.setSubmitDisabled(true);

    const quantity = this.getVisibleQuantity();
    const paymentType = this.getCurrentPaymentType();
    const isSubscription = paymentType === 'subscription';
    const deliveryFrequency = this.getDeliveryFrequencyDropdown()?.getCurrentSelection?.() || null;
    const absorbency = this.getAbsorbencySelector()?.getCurrentSelection?.() || null;
    const bundleVariantSelection = this.getBundleVariantSelector()?.getCurrentSelection?.() || null;

    if (this.quantityInput) {
      this.quantityInput.value = String(quantity);
    }

    const baseVariantId = this.getBaseVariantId();
    let nextVariantId = baseVariantId;
    let nextSellingPlanId = null;
    let nextProperties = {};
    let isValid = Boolean(quantity > 0);

    this.debug('Starting sync pass', {
      version,
      quantity,
      paymentType,
      isSubscription,
      isStandardRechargeProduct: this.isStandardRechargeProduct,
      hasDeliveryFrequencyProducts: this.hasDeliveryFrequencyProducts,
      baseVariantId,
      deliveryFrequency,
      absorbency: absorbency
        ? {
            currentCount: absorbency.currentCount,
            maxCount: absorbency.maxCount,
            selectionValid: absorbency.selectionValid,
            selections: (absorbency.selections || []).map((selection) => ({
              label: selection.label,
              quantity: selection.quantity,
              bundleVariantId: selection.bundleVariantId
            }))
          }
        : null,
      bundleVariantSelection
    });

    if (this.isStandardRechargeProduct) {
      nextSellingPlanId = isSubscription
        ? deliveryFrequency?.sellingPlanId || this.initialStandardSellingPlanId || null
        : null;
      isValid = isValid && Boolean(nextVariantId);

      if (isSubscription) {
        isValid = isValid && Boolean(nextSellingPlanId);
      }

      this.debug('Resolved standard Recharge state', {
        nextVariantId,
        nextSellingPlanId,
        isValid,
        deliveryFrequency
      });
    } else {
      const bundleState = await this.buildBundleState({
        isSubscription,
        quantity,
        deliveryFrequency,
        absorbency,
        bundleVariantSelection
      });

      if (version !== this.syncVersion) return;

      nextVariantId = bundleState.variantId;
      nextSellingPlanId = bundleState.sellingPlanId;
      nextProperties = bundleState.properties;
      isValid = isValid && bundleState.valid;

      this.debug('Resolved bundle Recharge state', {
        bundleState,
        isValid
      });
    }

    if (!nextVariantId) {
      isValid = false;
    }

    const nextRenderedStateKey = this.buildStateKey({
      variantId: nextVariantId || this.initialVariantId || '',
      quantity,
      sellingPlanId: nextSellingPlanId || '',
      properties: nextProperties,
      valid: isValid
    });

    if (this.lastRenderedStateKey !== nextRenderedStateKey) {
      if (this.variantInput) {
        const previousVariantId = this.variantInput.value;
        this.variantInput.value = nextVariantId || this.initialVariantId || '';
        if (previousVariantId !== this.variantInput.value) {
          this.debug('Updated variant input', {
            previousVariantId,
            nextVariantId: this.variantInput.value
          });
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.setSellingPlan(nextSellingPlanId);
      this.setProperties(nextProperties);
      this.lastRenderedStateKey = nextRenderedStateKey;
    }

    this.debug('Completed sync pass', {
      version,
      nextVariantId,
      nextSellingPlanId,
      nextProperties,
      isValid
    });

    this.setSubmitDisabled(!isValid);
  }

  /**
   * Builds the bundle-specific variant, selling plan, properties, and `_rb_id` payload used
   * by accelerated checkout for Recharge bundle products.
   *
   * @param {Object} params
   * @param {boolean} params.isSubscription
   * @param {number} params.quantity
   * @param {Object|null} params.deliveryFrequency
   * @param {Object|null} params.absorbency
   * @param {Object|null} params.bundleVariantSelection
   * @returns {Promise<{variantId: string, sellingPlanId: string|null, properties: Object, valid: boolean}>}
   */
  async buildBundleState({ isSubscription, quantity, deliveryFrequency, absorbency, bundleVariantSelection }) {
    const oneTimeProductId = this.oneTimePurchaseProductId;
    const oneTimeVariantId = this.oneTimePurchaseVariantId;
    const subscriptionProductId = deliveryFrequency?.frequencyProductId || '';
    const subscriptionVariantId = deliveryFrequency?.frequencyProductVariantId || '';
    const subscriptionSellingPlanId = deliveryFrequency?.sellingPlanId || '';
    const subscriptionSelectionQuantity = parseInt(deliveryFrequency?.maxQuantityMultiplier, 10);

    const productBundleData = {
      quantity,
      productId: isSubscription ? subscriptionProductId : oneTimeProductId,
      variantId: isSubscription ? subscriptionVariantId : oneTimeVariantId,
      sellingPlanId: isSubscription ? subscriptionSellingPlanId : null
    };

    let valid = Boolean(productBundleData.variantId);
    let cartProperties = {};
    let rechargeBundleData = null;

    if (isSubscription) {
      valid = valid && Boolean(productBundleData.sellingPlanId);
    }

    if (this.isAbsorbencyProduct) {
      const selectionValid = absorbency?.selectionValid !== false;
      valid = valid && selectionValid;

      if (selectionValid) {
        const absorbencySelections =
          this.productType === 'pad'
            ? this.getPadAbsorbencyData(absorbency)
            : this.getTamponAbsorbencyData(absorbency);

        cartProperties = absorbencySelections.cartProperties;
        rechargeBundleData = {
          externalProductId: productBundleData.productId,
          externalVariantId: productBundleData.variantId,
          selections: absorbencySelections.absorbencySelections
        };
      }
    } else if (bundleVariantSelection) {
      const selection = {
        collectionId: this.rechargeBundleCollectionId,
        quantity: parseInt(subscriptionSelectionQuantity, 10),
        externalProductId: bundleVariantSelection.subscriptionProductId,
        externalVariantId: bundleVariantSelection.variantId
      };

      rechargeBundleData = {
        externalProductId: productBundleData.productId,
        externalVariantId: productBundleData.variantId,
        selections: [selection]
      };

      if (!bundleVariantSelection.isDefault) {
        cartProperties[bundleVariantSelection.rechargeBundleVariantOptionName] =
          bundleVariantSelection.variantTitle;
      }
    }

    if (!valid || !rechargeBundleData) {
      this.debug('Bundle state is incomplete before requesting Recharge bundle id', {
        isSubscription,
        quantity,
        productBundleData,
        valid,
        hasRechargeBundleData: Boolean(rechargeBundleData),
        cartProperties
      });

      return {
        variantId: productBundleData.variantId,
        sellingPlanId: productBundleData.sellingPlanId,
        properties: cartProperties,
        valid: false
      };
    }

    const bundleRequestKey = this.buildStateKey(rechargeBundleData);

    if (bundleRequestKey === this.lastBundleRequestKey && this.lastBundleState) {
      this.debug('Reusing cached bundle state', {
        bundleRequestKey,
        bundleState: this.lastBundleState
      });
      return this.cloneBundleState(this.lastBundleState);
    }

    if (this.pendingBundleRequests.has(bundleRequestKey)) {
      const pendingBundleState = await this.pendingBundleRequests.get(bundleRequestKey);
      this.debug('Awaiting in-flight bundle state request', {
        bundleRequestKey,
        bundleState: pendingBundleState
      });
      return this.cloneBundleState(pendingBundleState);
    }

    try {
      this.debug('Requesting Recharge bundle id', {
        bundleRequestKey,
        rechargeBundleData
      });

      const bundleRequestPromise = this.getRechargeBundleId(rechargeBundleData).then(
        (rechargeBundleId) => ({
          variantId: productBundleData.variantId,
          sellingPlanId: productBundleData.sellingPlanId,
          properties: {
            ...cartProperties,
            _rb_id: rechargeBundleId
          },
          valid: Boolean(rechargeBundleId)
        })
      );

      this.pendingBundleRequests.set(bundleRequestKey, bundleRequestPromise);

      const bundleState = await bundleRequestPromise;
      this.lastBundleRequestKey = bundleRequestKey;
      this.lastBundleState = this.cloneBundleState(bundleState);

      this.debug('Received Recharge bundle state', {
        bundleRequestKey,
        bundleState
      });

      return bundleState;
    } catch (error) {
      this.debug('Recharge bundle id request failed', {
        bundleRequestKey,
        error: error?.message || error
      });
      console.warn('[recharge-product-form-sync] Failed to compute Recharge bundle id', error);

      return {
        variantId: productBundleData.variantId,
        sellingPlanId: productBundleData.sellingPlanId,
        properties: cartProperties,
        valid: false
      };
    } finally {
      this.pendingBundleRequests.delete(bundleRequestKey);
    }
  }

  /**
   * Converts tampon absorbency selections into Recharge bundle selections and cart properties.
   *
   * @param {Object|null} absorbency
   * @returns {{cartProperties: Object, absorbencySelections: Object[]}}
   */
  getTamponAbsorbencyData(absorbency) {
    const cartProperties = {};
    const absorbencySelections = (absorbency?.selections || [])
      .filter((option) => option.quantity > 0)
      .map((option) => {
        cartProperties[option.label] = parseInt(option.quantity, 10) * parseInt(option.increment, 10);

        return {
          quantity: option.quantity,
          externalProductId: this.rechargeBundleSubscriptionProductId,
          externalVariantId: option.bundleVariantId,
          collectionId: this.rechargeBundleCollectionId
        };
      });

    return {
      cartProperties,
      absorbencySelections
    };
  }

  /**
   * Converts pad absorbency selections into Recharge bundle selections and cart properties,
   * including the synthetic mixed absorbency line when both pad types are present.
   *
   * @param {Object|null} absorbency
   * @returns {{cartProperties: Object, absorbencySelections: Object[]}}
   */
  getPadAbsorbencyData(absorbency) {
    // Used to show the quantity of each absorbency option in the cart.
    const cartProperties = {};
    const selectedOptions = (absorbency?.selections || []).filter((option) => option.quantity > 0);

    selectedOptions.forEach((option) => {
      cartProperties[option.label] = parseInt(option.quantity, 10) * parseInt(option.increment, 10);
    });

    // Used to pass the absorbency options to the Recharge API to get the bundle id.
    const padAbsorbencySelections = [];
    const [firstOption, secondOption] = selectedOptions;

    // Customers select pads in 6-count increments, but Recharge expects 12-count bundle quantities.
    // Convert the overlap between the two absorbency types into the mixed bundle first.
    const firstQuantity = parseInt(firstOption?.quantity || 0, 10);
    const secondQuantity = parseInt(secondOption?.quantity || 0, 10);
    const mixedQuantity = Math.min(firstQuantity, secondQuantity);
    const remainingFirstQuantity = firstQuantity - mixedQuantity;
    const remainingSecondQuantity = secondQuantity - mixedQuantity;
    const firstFullBundleQuantity = remainingFirstQuantity / 2;
    const secondFullBundleQuantity = remainingSecondQuantity / 2;

    // After removing the mixed bundles, the remaining single-absorbency quantities should be
    // even so they can be represented as full 12-count bundles in Recharge.
    if (!Number.isInteger(firstFullBundleQuantity) || !Number.isInteger(secondFullBundleQuantity)) {
      console.warn('[recharge-product-form-sync] Pad absorbency selections could not be converted into 12-count Recharge bundles', {
        selectedOptions
      });
    }

    if (mixedQuantity > 0 && firstOption?.padsMixedVariantId) {
      padAbsorbencySelections.push({
        quantity: mixedQuantity,
        label: 'Mixed',
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: firstOption.padsMixedVariantId,
        collectionId: this.rechargeBundleCollectionId
      });
    }

    if (Number.isInteger(firstFullBundleQuantity) && firstFullBundleQuantity > 0 && firstOption) {
      padAbsorbencySelections.push({
        quantity: firstFullBundleQuantity,
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: firstOption.bundleVariantId,
        collectionId: this.rechargeBundleCollectionId
      });
    }

    if (Number.isInteger(secondFullBundleQuantity) && secondFullBundleQuantity > 0 && secondOption) {
      padAbsorbencySelections.push({
        quantity: secondFullBundleQuantity,
        externalProductId: this.rechargeBundleSubscriptionProductId,
        externalVariantId: secondOption.bundleVariantId,
        collectionId: this.rechargeBundleCollectionId
      });
    }

    return {
      cartProperties,
      // Remove any zero-quantity selections before sending the bundle request to Recharge.
      absorbencySelections: padAbsorbencySelections.filter((selection) => selection.quantity > 0)
    };
  }

  /**
   * Ensures the Recharge SDK is available and asks it for the bundle id that matches the
   * current bundle configuration.
   *
   * @param {Object} bundleData
   * @returns {Promise<string>}
   */
  async getRechargeBundleId(bundleData) {
    await this.waitForRechargeClient();

    if (!window.recharge?.bundle?.getBundleId) {
      throw new Error('Recharge bundle client is unavailable');
    }

    if (typeof window.recharge.init === 'function' && window.Shopify?.shop) {
      window.recharge.init({ storeIdentifier: window.Shopify.shop });
    }

    this.debug('Calling Recharge bundle client', { bundleData });
    return window.recharge.bundle.getBundleId(bundleData);
  }

  /**
   * Polls for the Recharge bundle client because it is loaded asynchronously from the CDN.
   *
   * @returns {Promise<void>}
   */
  waitForRechargeClient() {
    if (window.recharge?.bundle?.getBundleId) {
      this.debug('Recharge bundle client already available');
      return Promise.resolve();
    }

    this.debug('Waiting for Recharge bundle client');
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 80;

      const interval = window.setInterval(() => {
        attempts += 1;

        if (window.recharge?.bundle?.getBundleId) {
          window.clearInterval(interval);
          this.debug('Recharge bundle client became available', { attempts });
          resolve();
          return;
        }

        if (attempts >= maxAttempts) {
          window.clearInterval(interval);
          this.debug('Recharge bundle client timed out', { attempts });
          reject(new Error('Recharge bundle client timed out'));
        }
      }, 100);
    });
  }
}

if (!customElements.get('recharge-product-form-sync')) {
  customElements.define('recharge-product-form-sync', RechargeProductFormSync);
}
